import type { LocationQueryRaw } from 'vue-router';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

import type { TQueryKeyConfig } from '../constants';
import constants, { QUERY_KEY_TYPE, URL_MARKERS } from '../constants';
import {
  FILTER_NAVIGATION,
  FILTER_NAVIGATION_ACTIONS,
  FILTER_NAVIGATION_GETTERS,
} from '../store/filterNavigation/name';
import type { TPageKey, TPendingTransfer, TSavedPageFilters, TTransferFilters } from '../store/filterNavigation/types';

const { NAVIGATION_TYPE, PAGE_KEY, FILTER_STORAGE_KEY, URL_FILTER_KEYS } = constants;

const loadFromStorage = (pageKey: TPageKey): TSavedPageFilters | null => {
  const key = FILTER_STORAGE_KEY[pageKey as keyof typeof FILTER_STORAGE_KEY];
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as TSavedPageFilters) : null;
  } catch {
    return null;
  }
};

const parseQueryValue = (val: unknown): number[] => {
  if (val == null) return [];
  const arr = Array.isArray(val) ? val : [val];
  return arr.map(Number).filter((n) => !Number.isNaN(n) && n > 0);
};

const resolveFilters = (
  pageKey: TPageKey,
  navigationType: string | null,
  pendingTransfer: TPendingTransfer | null,
  savedFilters: Partial<Record<TPageKey, TSavedPageFilters | null>>,
): TSavedPageFilters | null => {
  switch (pageKey) {
    case PAGE_KEY.STATISTICS:
      return navigationType === NAVIGATION_TYPE.BUTTON ? pendingTransfer?.filters ?? null : null;
    case PAGE_KEY.LIST:
    case PAGE_KEY.MAP:
      return navigationType === NAVIGATION_TYPE.BUTTON && pendingTransfer !== null
        ? pendingTransfer.filters
        : savedFilters[pageKey] ?? loadFromStorage(pageKey);
    default:
      return null;
  }
};

export const usePageFilters = (pageKey: TPageKey) => {
  const store = useStore();
  const router = useRouter();
  const route = useRoute();

  const keyConfigs: TQueryKeyConfig[] = URL_FILTER_KEYS[pageKey] ?? [];

  const readQueryParams = (): TSavedPageFilters | null => {
    const result: Record<string, unknown> = {};

    for (const { key, type } of keyConfigs) {
      const raw = route.query[key];
      if (raw == null) continue;

      if (type === QUERY_KEY_TYPE.SCALAR) {
        const id = Number(raw);
        if (!Number.isNaN(id) && id > 0) result[key] = id;
      } else {
        const ids = parseQueryValue(raw);
        if (ids.length) result[key] = ids;
      }
    }

    return Object.keys(result).length > 0 ? (result as TSavedPageFilters) : null;
  };

  let _markSavedOnNextUpdate = false;

  const updateQueryParams = (filters: TSavedPageFilters, markSaved = false): void => {
    const shouldMarkSaved = markSaved || _markSavedOnNextUpdate;
    _markSavedOnNextUpdate = false;

    const query: LocationQueryRaw = { ...route.query };
    // Удаляем маркер, который нужен для того, чтобы не подтягивались фильтры из store/localStorage
    delete query[URL_MARKERS.FILTER_RESET];

    if (shouldMarkSaved) {
      query[URL_MARKERS.SAVED] = '1';
    }

    for (const { key, type } of keyConfigs) {
      const val = (filters as Record<string, unknown>)[key];

      if (type === QUERY_KEY_TYPE.SCALAR) {
        if (typeof val === 'string' || typeof val === 'number') {
          query[key] = String(val);
        } else {
          delete query[key];
        }
      } else if (Array.isArray(val) && val.length > 0) {
        query[key] = val as string[];
      } else {
        delete query[key];
      }
    }

    router.replace({ query });
  };

  const consumeFilters = (): TSavedPageFilters | null => {
    const navigationType = store.getters[FILTER_NAVIGATION_GETTERS.getLastNavigationType];
    const pendingTransfer = store.getters[FILTER_NAVIGATION_GETTERS.getPendingTransfer];
    const savedFilters = store.getters[FILTER_NAVIGATION_GETTERS.getSavedFilters];

    store.commit(`${FILTER_NAVIGATION}/clearPendingTransfer`);

    if (route.query[URL_MARKERS.FILTER_RESET]) return readQueryParams();

    // Кнопочная навигация (мз карты в перечень, из статистики в перечень и проч) - берем из pendingTransfer
    if (pendingTransfer) return resolveFilters(pageKey, NAVIGATION_TYPE.BUTTON, pendingTransfer, savedFilters);

    // Возврат из карточки или браузерный back:
    // приоритет store/localStorage (полный набор фильтров, включая доп поля)
    if (navigationType === NAVIGATION_TYPE.BACK) {
      const backFilters = resolveFilters(pageKey, navigationType, null, savedFilters);
      if (backFilters) _markSavedOnNextUpdate = true;
      return backFilters ?? (pageKey !== PAGE_KEY.STATISTICS ? readQueryParams() : null);
    }

    // Рефреш с флагом _saved: в localStorage лежит полный снимок фильтра / или выставляем по квери
    if (route.query[URL_MARKERS.SAVED]) return loadFromStorage(pageKey) ?? readQueryParams();

    const fromQuery = readQueryParams();
    if (fromQuery) return fromQuery;

    const fromStorage = loadFromStorage(pageKey);
    if (fromStorage) _markSavedOnNextUpdate = true;
    return fromStorage;
  };

  const saveFilters = (filters: TSavedPageFilters): void => {
    store.dispatch(FILTER_NAVIGATION_ACTIONS.savePageFilters, { pageKey, filters });
    updateQueryParams(filters, true);
  };

  // Сохраняет фильтры в store/localStorage без записи в URL для перечня
  const saveToStore = (filters: TSavedPageFilters): void => {
    store.dispatch(FILTER_NAVIGATION_ACTIONS.savePageFilters, { pageKey, filters });
  };

  const navigateWithFilters = (targetPage: TPageKey, transferFilters: TTransferFilters): void => {
    store.dispatch(FILTER_NAVIGATION_ACTIONS.setTransferFilters, {
      targetPage,
      filters: transferFilters,
    });
  };

  const wasButtonNavigation = (): boolean =>
    store.getters[FILTER_NAVIGATION_GETTERS.getLastNavigationType] === NAVIGATION_TYPE.BUTTON;

  const wasBackNavigation = (): boolean =>
    store.getters[FILTER_NAVIGATION_GETTERS.getLastNavigationType] === NAVIGATION_TYPE.BACK;

  return {
    consumeFilters,
    saveFilters,
    saveToStore,
    navigateWithFilters,
    updateQueryParams,
    wasButtonNavigation,
    wasBackNavigation,
  };
};
